<template>
  <header>
    <PostFeaturedImage
      v-if="audioData.featuredImage"
      :class="$style.audioCover"
      :is-aspect="true"
      :is-wide="audioData.featuredImageWidth === 'wide'"
      :featured-image="audioData.featuredImage.node"
      :srcset="audioData.imageSrcset"
      :ga4-data="
        JSON.stringify({
          click_type: 'hero',
          component: 'AudioHeader',
          content_group: 'audio',
          gtm_tag: 'image',
          file_name: `${audioData.featuredImage.node.sourceUrl.split('/').pop()}`,
        })
      "
    ></PostFeaturedImage>
    <div :class="$style.header">
      <nav>
        <BackLink
          component="AudioHeader"
          group="audio"
          label="Audio"
          name="AudioSection"
        ></BackLink>
      </nav>
      <div :class="$style.audioTitle">
        <h1 v-html="audioData.title"></h1>
        <button
          type="button"
          class="button-reset"
          :aria-label="`${isPlaying ? 'Pause' : 'Play'}`"
          @click.prevent="prepareAudio($event)"
        >
          <KeepAlive>
            <IconPlayCircle
              v-if="!isPlaying || !isCurrentTrackPlaying"
              :class="$style.svg"
            ></IconPlayCircle>
            <IconPauseCircle v-else :class="$style.svg"></IconPauseCircle>
          </KeepAlive>
        </button>
      </div>
      <div :class="[$style.postMeta, 'subtitle-2', 'space-0-above']">
        <span v-if="isPlaylist">{{ audioData.tracks.length }} tracks</span>
        <time :datetime="formatTimeDuration(audioData.duration)">
          {{ formatDuration(audioData.duration) }}
        </time>
        <span>{{ audioData.scCreatedAt }}</span>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { AudioData } from '@/types/audio.interface';
import { AudioState } from '@/types/audio.store.interface';
import BackLink from '@/components/common/BackLink.vue';
import { computed } from 'vue';
import { formatDuration, formatTimeDuration } from '@/utils';
import IconPauseCircle from '@/assets/svgs/IconPauseCircle.svg?component';
import IconPlayCircle from '@/assets/svgs/IconPlayCircle.svg?component';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import { storeToRefs } from 'pinia';
import { useAudioStore } from '@/stores/audioStore';

const props = defineProps<{
  audioData: AudioData;
}>();

const store = useAudioStore();
const { audioElement, audioId, isPlaying } = storeToRefs(store);
const { updateAudioState } = store;

const isCurrentTrackPlaying = computed(() => {
  if (audioId && audioId.value && props.audioData.__typename.includes('track')) {
    return audioId.value === props.audioData.databaseId;
  }

  if (audioId && audioId.value && isPlaylist.value) {
    if (audioId.value === props.audioData.databaseId) {
      return true;
    }

    return (
      audioId.value === props.audioData.databaseId &&
      props.audioData.tracks.findIndex((track) => +track.trackId === audioId.value) !== -1
    );
  }

  return false;
});
const isPlaylist = computed(() => props.audioData.__typename === 'Audio_playlist');

function prepareAudio(event: PointerEvent | MouseEvent): void {
  updateAudioState({ isInitial: false, lastFocused: event.target });

  const preparedAudioData: AudioState = {};

  if (audioId && audioId.value !== props.audioData.databaseId) {
    if (audioElement && audioElement.value) {
      // reset
      audioElement.value.removeAttribute('src');
      audioElement.value.pause();
      audioElement.value.currentTime = 0;
      audioElement.value.load();
    }

    preparedAudioData.audioId = props.audioData.databaseId;
    preparedAudioData.currentTrack = 0;
    preparedAudioData.isPlaying = true;
    preparedAudioData.playlistTitle = isPlaylist.value ? props.audioData.title : '';
    preparedAudioData.tracks = isPlaylist.value ? props.audioData.tracks : [props.audioData];
  } else {
    if (isPlaying) {
      preparedAudioData.isPlaying = !isPlaying.value;
    }
  }

  preparedAudioData.source = 'permalink';
  updateAudioState(preparedAudioData);
}
</script>

<style lang="scss" module>
.audio-cover {
  background-color: $white;
}

.audio-title {
  align-items: center;
  display: grid;
  grid-template-areas: 'a b';
  grid-template-columns: 6rem 1fr;

  button {
    grid-area: a;
    height: 6rem;
    width: 6rem;
  }

  h1 {
    text-align: left;
    grid-area: b;
    margin-left: 0.5rem;
  }
}

.svg {
  cursor: pointer;
  display: inline-block;
  flex-shrink: 0;
  height: 6rem;
  width: 6rem;

  &:hover {
    path {
      fill: $black-hover;
    }
  }
}

.post-meta {
  > * {
    &::after {
      content: '|';
      margin: 0 0.25rem;
    }

    &:last-child {
      &::after {
        content: '';
      }
    }
  }

  a {
    text-decoration: none;

    &:hover {
      text-decoration: none;

      svg {
        fill: $black-hover;
      }
    }
  }

  svg {
    fill: $black;
    margin-right: 0.5rem;
    height: 0.75rem;
    width: 0.75rem;
  }
}

@media (min-width: $break-point) {
  .audio-title {
    flex-wrap: wrap;

    button,
    svg {
      height: 6rem;
      width: 6rem;
    }
  }

  .header {
    @include grid;

    > * {
      grid-column: 1 / span 8;
    }

    nav {
      grid-column: span 12;
    }
  }
}
</style>
